import React, { useState } from 'react';
import { useStyles } from './style';
import { useTheme, Typography } from '@forgeglobal/design-framework';
import Table from '@tcs/client-portal-common/components/molecules/Table';
import transformDebitCredit from '@tcs/client-portal-common/utils/transformDebitCredit';
import useAllTransactions from '@tcs/client-portal-common/hooks/useAllTransactions';
import Spinner from '@tcs/common/components/atoms/Spinner';
import BaseHeader from '@tcs/client-portal-common/components/molecules/Table/cells/headers/BaseHeader';
import DateCell from '@tcs/client-portal-common/components/molecules/Table/cells/DateCell';
import DebitCreditCell from '@tcs/client-portal-common/components/molecules/Table/cells/DebitCreditCell';
import TwoLineCell from '@tcs/client-portal-common/components/molecules/Table/cells/TwoLineCell';
import BaseCell from '@tcs/client-portal-common/components/molecules/Table/cells/BaseCell';

const columns = [
  {
    accessor: 'date',
    header: () => <BaseHeader text="Date" />,
    cell: (info) => <DateCell value={info.getValue()} />,
    size: 150,
  },
  {
    accessor: 'description',
    header: () => <BaseHeader text="Description" />,
    cell: (info) => (
      <TwoLineCell
        primary={info.getValue().primary}
        secondary={info.getValue().secondary}
        isPending={info.row.original?.pending} // use pending colors (see depositCss)
      />
    ),
    size: 300,
  },
  {
    accessor: 'accountType',
    header: () => <BaseHeader text="Account Type" />,
    cell: (info) => <BaseCell value={info.getValue()} />,
    size: 150,
  },
  {
    accessor: 'accountNumber',
    header: () => <BaseHeader text="Account Number" />,
    cell: (info) => <BaseCell value={info.getValue()} />,
    size: 150,
  },
  {
    accessor: 'amount',
    header: () => <BaseHeader text="Amount" align="right" />,
    cell: (info) => <DebitCreditCell value={info.getValue()} />,
    size: 150,
  },
];

interface Props {
  accountStatus: string;
}

const AccountsActivityTable = ({ accountStatus }: Props) => {
  const classes = useStyles();
  const theme = useTheme();

  const [page, setPage] = useState(1);
  const { data: transactions, loading } = useAllTransactions(
    page,
    10,
    accountStatus
  );

  if (loading) {
    return <Spinner />;
  }

  if (transactions == null) {
    return null;
  }

  const formattedTransactions = transformDebitCredit(transactions.data);

  // custom row styling if deposit has not cleared
  const depositCss = function (row) {
    return row.original?.pending ? { color: theme.palette.text.disabled } : {};
  };

  return (
    <div>
      <Typography
        variant="h5"
        color="textPrimary"
        className={classes.title}
        data-test-id="activity-table-title">
        All Recent Transactions
      </Typography>
      <Table
        columns={columns}
        data={formattedTransactions}
        customRowCssFunction={depositCss}
        loading={loading}
        page={page}
        setPage={setPage}
        totalItems={transactions.totalCount}
      />
    </div>
  );
};

export default AccountsActivityTable;
